import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { AdminUser, AdminRole } from '@/types/database'

export function useAdminUsers() {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['admin-users'],
    queryFn: async (): Promise<AdminUser[]> => {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: true })

      if (error) throw error
      return data as AdminUser[]
    },
  })

  const updateRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: AdminRole }) => {
      const { error } = await supabase
        .from('admin_users')
        .update({ role })
        .eq('id', userId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })

  const deleteUser = useMutation({
    mutationFn: async (userId: string) => {
      // Server-side removal: deletes admin_users + auth.users so the email
      // can be re-invited later. Direct DELETE on admin_users would orphan
      // the auth.users row and block "User already registered" on re-invite.
      const { data, error } = await supabase.functions.invoke('remove-admin', {
        body: { user_id: userId },
      })

      if (error) {
        const fnMessage =
          (data && typeof data === 'object' && 'error' in data && typeof (data as { error: unknown }).error === 'string')
            ? (data as { error: string }).error
            : error.message
        throw new Error(fnMessage)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })

  return { ...query, updateRole, deleteUser }
}
